import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ChangementDePapier, Papier } from '../partage/api/types';
import {
  aPasseLeSeuil,
  cadreTire,
  changementDuGeste,
  placeGlissee,
  type Cadre,
  type Point,
} from './glisserUnPapier';

/**
 * Ce que la carte donne aux papiers pour qu'on les glisse : le passage de l'écran à la carte
 * (celui de la caméra, `carte/cameraCarte.ts`, `versLaCarte`). Aucun mur : un papier va où on
 * le lâche, et le regard va jusqu'à lui (retour d'Igor du 2026-10-20).
 */
export interface CarteDesPapiers {
  versLaCarte: (clientX: number, clientY: number) => Point | null;
}

type Geste = 'glisser' | 'poignee';

interface Tir {
  geste: Geste;
  pointeur: number;
  /** Où la main s'est posée, à l'écran et sur la carte. */
  appui: Point;
  appuiSurLaCarte: Point;
  /** Le papier tel qu'il était à l'appui (sa hauteur vue, texte compris, pour la poignée). */
  depart: Cadre;
  /** Ce qu'il y a à envoyer au lâcher. */
  avant: Cadre;
  parti: boolean;
}

/**
 * Glisser un papier et tirer sa poignée (chantier post-it-et-listes, ticket 03), pour les deux
 * sortes de papier : le post-it, et la liste libre du ticket 04.
 *
 * Un appui dans le papier (hors de ses boutons et d'un champ où l'on écrit déjà) ne glisse
 * qu'au-delà de `SEUIL_DU_GLISSER_PX` : en deçà, c'est un clic, on écrit. Pendant le geste, le
 * papier suit la main, au zoom du moment ; rien ne part au serveur. **Au lâcher, une fois**, ce
 * qui a changé part (`onPoser`) : la place pour un glisser, la taille et la place qui la suit
 * pour la poignée. Le papier garde sa place tirée jusqu'à la réponse du serveur, puis montre la
 * lecture : si l'enregistrement a échoué, elle a repris la place d'avant (`usePapiers`).
 *
 * Le regard ne bouge pas : la caméra ignore un appui parti d'un papier (`cameraCarte.ts`).
 */
export function useGlisserUnPapier(
  papier: Papier,
  carte: CarteDesPapiers,
  onPoser: (id: string, changement: ChangementDePapier) => Promise<void>
) {
  const element = useRef<HTMLDivElement>(null);
  /** Le cadre montré pendant le geste, et après jusqu'à ce que la lecture le rende. */
  const [tire, setTire] = useState<Cadre | null>(null);
  /** Le même, lu au lâcher sans attendre que React l'ait dessiné. */
  const montre = useRef<Cadre | null>(null);
  const montrer = useCallback((cadre: Cadre | null) => {
    montre.current = cadre;
    setTire(cadre);
  }, []);
  const [geste, setGeste] = useState<Geste | null>(null);
  const tir = useRef<Tir | null>(null);
  /** Le dernier écart de la poignée, pour se recaler quand le texte fait grandir le papier. */
  const ecartDeLaPoignee = useRef<Point | null>(null);
  const actuel = useRef({ carte, onPoser, papier });
  actuel.current = { carte, onPoser, papier };

  const commencer = useCallback((evenement: React.PointerEvent, quel: Geste) => {
    if (evenement.button !== 0) return;
    const cible = evenement.target as HTMLElement;
    // Ses boutons gardent leur clic ; dans un champ où l'on écrit déjà, on choisit du texte.
    if (quel === 'glisser') {
      if (cible.closest('.papier-outils, .papier-poignee')) return;
      if (cible.matches('input, textarea') && cible === document.activeElement) return;
    }
    const surLaCarte = actuel.current.carte.versLaCarte(evenement.clientX, evenement.clientY);
    const el = element.current;
    if (!surLaCarte || !el) return;
    // Là où il se voit : le geste d'avant peut attendre encore la réponse du serveur.
    const p = actuel.current.papier;
    const vu = montre.current ?? { x: p.x, y: p.y, largeur: p.largeur, hauteur: p.hauteur };
    tir.current = {
      geste: quel,
      pointeur: evenement.pointerId,
      appui: { x: evenement.clientX, y: evenement.clientY },
      appuiSurLaCarte: surLaCarte,
      depart: { ...vu, hauteur: Math.max(vu.hauteur, el.offsetHeight) },
      avant: vu,
      parti: false,
    };
    // La poignée n'a rien à écrire : elle part tout de suite, sans choisir de texte.
    if (quel === 'poignee') evenement.preventDefault();
  }, []);

  useEffect(() => {
    const bouger = (evenement: PointerEvent) => {
      const t = tir.current;
      if (!t || evenement.pointerId !== t.pointeur) return;
      if (evenement.buttons === 0) {
        lever(evenement);
        return;
      }
      if (!t.parti) {
        if (!aPasseLeSeuil(t.appui, { x: evenement.clientX, y: evenement.clientY })) return;
        t.parti = true;
        setGeste(t.geste);
        // On ne choisit pas de texte en glissant ; ce qui s'écrivait part à la sortie du champ.
        window.getSelection()?.removeAllRanges();
        const actif = document.activeElement as HTMLElement | null;
        if (actif && element.current?.contains(actif)) actif.blur();
      }
      evenement.preventDefault();
      const ici = actuel.current.carte.versLaCarte(evenement.clientX, evenement.clientY);
      if (!ici) return;
      const ecart = { x: ici.x - t.appuiSurLaCarte.x, y: ici.y - t.appuiSurLaCarte.y };
      if (t.geste === 'glisser') {
        const place = placeGlissee(t.depart, ecart);
        montrer({ ...t.avant, ...place });
      } else {
        ecartDeLaPoignee.current = ecart;
        montrer(cadreTire(t.depart, ecart));
      }
    };
    const lever = (evenement: PointerEvent) => {
      const t = tir.current;
      if (!t || evenement.pointerId !== t.pointeur) return;
      tir.current = null;
      ecartDeLaPoignee.current = null;
      setGeste(null);
      if (!t.parti) return;
      // Le clic qui suit un glisser n'ouvre rien (une bulle sous laquelle le papier passe).
      const avaler = (clic: MouseEvent) => {
        clic.stopPropagation();
        clic.preventDefault();
      };
      window.addEventListener('click', avaler, { capture: true, once: true });
      window.setTimeout(() => window.removeEventListener('click', avaler, { capture: true }), 0);
      const cadre = montre.current;
      const changement = cadre ? changementDuGeste(t.avant, cadre) : null;
      if (!changement) {
        montrer(null);
        return;
      }
      // Le serveur a répondu, oui ou non : on montre ce que dit la lecture (la place gardée, ou
      // celle d'avant après un refus). D'ici là, le papier reste où la main l'a lâché.
      void actuel.current.onPoser(actuel.current.papier.id, changement).finally(() => {
        if (!tir.current) montrer(null);
      });
    };
    window.addEventListener('pointermove', bouger);
    window.addEventListener('pointerup', lever);
    window.addEventListener('pointercancel', lever);
    return () => {
      window.removeEventListener('pointermove', bouger);
      window.removeEventListener('pointerup', lever);
      window.removeEventListener('pointercancel', lever);
    };
  }, [montrer]);

  // La poignée : un texte plus haut que la hauteur choisie fait grandir le papier vers le bas ;
  // une fois redessiné, on le mesure, et son haut reste où il était.
  useLayoutEffect(() => {
    const t = tir.current;
    const ecart = ecartDeLaPoignee.current;
    const el = element.current;
    if (!t || t.geste !== 'poignee' || !ecart || !el || !tire) return;
    const recale = cadreTire(t.depart, ecart, el.offsetHeight);
    if (recale.y !== tire.y) montrer(recale);
  }, [tire, montrer]);

  const cadre: Cadre = tire ?? { x: papier.x, y: papier.y, largeur: papier.largeur, hauteur: papier.hauteur };
  return {
    element,
    cadre,
    geste,
    surLePapier: (evenement: React.PointerEvent) => commencer(evenement, 'glisser'),
    surLaPoignee: (evenement: React.PointerEvent) => commencer(evenement, 'poignee'),
  };
}
